import React from 'react';

import clsxm from '@/lib/clsxm';

const ROTATE_TRANSITION_CLASS = 'transition transform duration-200';

export enum IconColor {
  PRIMARY = 'PRIMARY',
  PRIMARY_EXTRA_DARK = 'PRIMARY_EXTRA_DARK',
  TEXT = 'TEXT',
  WHITE = 'WHITE',
  INHERIT = 'INHERIT',
  INDIGO = 'INDIGO',
  ULTRAVIOLET = 'ULTRAVIOLET',
}

export enum IconSize {
  XS = 'xs',
  SMALL = 'small',
  LARGE = 'large',
  XL = 'extra-large',
  XXL = '2-extra-large',
  DEFAULT = 'default',
}

export enum IconRotate {
  DEG_45 = 45,
  DEFAULT = 0,
}

export enum IconInline {
  INLINE = 'inline',
  DEFAULT = 'default',
}

const colorMap: Record<IconColor, string> = {
  [IconColor.PRIMARY]: 'text-primary',
  [IconColor.PRIMARY_EXTRA_DARK]: 'text-primary-extraDark',
  [IconColor.TEXT]: 'text-typography',
  [IconColor.WHITE]: 'text-white',
  [IconColor.INHERIT]: 'text-inherit',
  [IconColor.INDIGO]: 'text-indigo',
  [IconColor.ULTRAVIOLET]: 'text-ultraviolet',
};

const sizeMap = Object.freeze<{ [key in IconSize]: string }>({
  [IconSize.XS]: 'w-3 h-3',
  [IconSize.SMALL]: 'w-4 h-4',
  [IconSize.LARGE]: 'w-6 h-6',
  [IconSize.XL]: 'w-8 h-8',
  [IconSize.XXL]: 'w-10 h-10',
  [IconSize.DEFAULT]: 'w-6 h-6',
});

const rotateMap = Object.freeze<{ [key in IconRotate]: string }>({
  [IconRotate.DEG_45]: 'rotate-45',
  [IconRotate.DEFAULT]: '',
});

const inlineMap = Object.freeze<{ [key in IconInline]: string }>({
  [IconInline.INLINE]: 'mr-1 inline',
  [IconInline.DEFAULT]: '',
});

export type IconProps = {
  color?: IconColor;
  size?: IconSize;
  rotate?: IconRotate;
  inline?: boolean;
  className?: string;
};

type BaseIconProps = {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
} & IconProps;

const BaseIcon = ({
  Icon,
  color,
  size,
  inline,
  rotate,
  className,
}: BaseIconProps) => {
  const sizeClass = sizeMap[size || IconSize.DEFAULT];
  const rotateClass = rotateMap[rotate || IconRotate.DEFAULT];
  const colorClass = colorMap[color || IconColor.PRIMARY];
  const inlineClass =
    inlineMap[inline ? IconInline.INLINE : IconInline.DEFAULT];

  return (
    <Icon
      className={clsxm(
        sizeClass,
        ROTATE_TRANSITION_CLASS,
        rotateClass,
        colorClass,
        inlineClass,
        className
      )}
      aria-hidden='true'
    />
  );
};
export default BaseIcon;
